import { Component, Input, OnInit, inject, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { TestingTasksComponent } from '../../testing-tasks/testing-tasks.component';
import { SingleCodeField, generalFieldsData, IndividualField, IndividualReferenceField, formMetaData } from '../../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../shared/error.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../../shared/state.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../shared/auth/auth.service';
import { NotificationService } from '../../shared/notification/notification.service';

@Component({
    selector: 'app-care-plan-form',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        DynamicFormsV2Component,
        TestingTasksComponent
    ],
    templateUrl: './care-plan-form.component.html',
    styleUrl: './care-plan-form.component.scss'
})
export class CarePlanFormComponent implements OnInit {
    @Input() carePlanFormFields: any;
    @Input() admissionSubmitted: boolean = false;
    @Input() carePlanId?: string; // Care Plan ID if it exists

    displayTaskForm = false;
    formfieldService = inject(FormFieldsSelectDataService);
    errorService = inject(ErrorService);
    http = inject(HttpClient);
    backendUrl = inject(backendEndPointToken);
    stateService = inject(StateService);

    // Task focus options (service request selection)
    taskFocusOptions: Array<{ code: string; display: string }> = [
        { code: 'create-new-service-request', display: 'Create New Service Request' },
        { code: 'select-existing-service-request', display: 'Select Existing Service Request' },
        { code: 'create-new-medication-request', display: 'Create New Medication Request' },
        { code: 'select-existing-medication-request', display: 'Select Existing Medication Request' }
    ];

    selectedTaskFocusOption: string = '';
    existingServiceRequests: any[] = [];
    existingMedicationRequests: any[] = [];

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any,
        @Optional() public dialogRef?: MatDialogRef<CarePlanFormComponent>
    ) {
        // Check if carePlanId is passed via dialog data
        if (this.dialogData?.carePlanId) {
            this.carePlanId = this.dialogData.carePlanId;
        }
        // Check if admissionSubmitted is passed via dialog data
        if (this.dialogData?.admissionSubmitted !== undefined) {
            this.admissionSubmitted = this.dialogData.admissionSubmitted;
        }
    }

    ngOnInit() {
        // Component initialization
        return forkJoin({
            //   'location': this.formfieldService.getFormFieldSelectData('admission', 'location'),
            //   'admitSource': this.formfieldService.getFormFieldSelectData('admission', 'admitSource'),
            'carePlanStatus': this.formfieldService.getFormFieldSelectData('admission', 'carePlanStatus'),
            'carePlanIntent': this.formfieldService.getFormFieldSelectData('admission', 'carePlanIntent'),
            //   'admissionReason': this.formfieldService.getFormFieldSelectData('encounter', 'reason'),
            //   'taskIntent': this.formfieldService.getFormFieldSelectData('admission', 'taskIntent'),
            //   'taskPriority': this.formfieldService.getFormFieldSelectData('admission', 'taskPriority'),
            //   'taskStatus': this.formfieldService.getFormFieldSelectData('admission', 'taskStatus'),
            //   'taskCode': this.formfieldService.getFormFieldSelectData('admission', 'taskCode')
        })

            .subscribe({
                next: (g) => {
                    this.carePlanFormFields = {
                        'carePlan': {
                            formFields: [
                                <SingleCodeField>{
                                    generalProperties: <generalFieldsData>{
                                        fieldApiName: 'intent',
                                        fieldName: "Intent",
                                        fieldType: 'SingleCodeField',
                                        value: 'order',
                                        validations: [
                                            {
                                                type: 'default',
                                                name: 'required',
                                                isFunction: false
                                            }
                                        ],
                                        isArray: false,
                                        isGroup: false,

                                        auth: {
                                            read: 'all',
                                            write: 'doctor, nurse'
                                        },
                                    },
                                    data: g.carePlanIntent
                                },
                                // status of care plan
                                <SingleCodeField>{
                                    generalProperties: <generalFieldsData>{
                                        fieldApiName: 'status',
                                        fieldName: "Status",
                                        fieldType: 'SingleCodeField',
                                        value: 'active',
                                        isArray: false,
                                        isGroup: false,
                                        validations: [
                                            {
                                                type: 'default',
                                                name: 'required',
                                                isFunction: false
                                            }
                                        ],
                                        auth: {
                                            read: 'all',
                                            write: 'doctor, nurse'
                                        },
                                    },
                                    data: g.carePlanStatus
                                },
                                <IndividualField>{
                                    generalProperties: <generalFieldsData>{
                                        fieldApiName: 'title',
                                        fieldName: "Title",
                                        validations: [
                                            {
                                                type: 'default',
                                                name: 'required',
                                                isFunction: false
                                            }
                                        ],
                                        fieldLabel: "Give Your Plan A Title",
                                        fieldType: 'IndividualField',
                                        inputType: 'text',
                                        isArray: false,
                                        isGroup: false,

                                        auth: {
                                            read: 'all',
                                            write: 'doctor, nurse'
                                        },
                                    },
                                },
                                <IndividualField>{
                                    generalProperties: <generalFieldsData>{
                                        fieldApiName: 'description',
                                        fieldName: "Description",
                                        fieldLabel: "Describe Your Care Plan",
                                        fieldType: 'IndividualField',
                                        inputType: 'textarea',
                                        isArray: false,
                                        isGroup: false,

                                        auth: {
                                            read: 'all',
                                            write: 'doctor, nurse'
                                        },
                                    },
                                },

                            ],
                            formMetadata: <formMetaData>{
                                formName: 'carePlan',
                                formDescription: 'Care Plan Form',
                                formLabel: 'Care Plan Form',
                                showSubmitButton: true,
                                submitText: 'Submit Care Plan',

                            }
                        }
                    }
                    console.log('Loaded care plan form fields', this.carePlanFormFields);
                }, error: (err) => {
                    console.error('Error loading form field select data', err);
                    this.errorService.openandCloseError('Failed to load form data');
                    // this.error
                }
            })

    }

    /**
     * Fetch existing service requests for the current patient and encounter
     */
    async fetchExistingServiceRequests(): Promise<void> {
        try {
            const patientId = await firstValueFrom(this.stateService.currentPatientId$);
            const currentEncounter = this.stateService.currentEncounter.value;
            const encounterId = currentEncounter?.id;

            if (!patientId) {
                this.errorService.openandCloseError('No patient selected');
                return;
            }

            if (!encounterId) {
                this.errorService.openandCloseError('No encounter selected');
                return;
            }

            const response = await firstValueFrom(
                this.http.get<any>(`${this.backendUrl}/ServiceRequest?patient=${patientId}&encounter=Encounter/${encounterId}&status=active`)
            );

            this.existingServiceRequests = response.entry?.map((entry: any) => ({
                reference: `ServiceRequest/${entry.resource.id}`,
                display: entry.resource.code?.text || entry.resource.code?.coding?.[0]?.display || `Service Request ${entry.resource.id}`,
                resource: entry.resource
            })) || [];
        } catch (error) {
            console.error('Error fetching service requests:', error);
            this.errorService.openandCloseError('Failed to fetch service requests');
        }
    }

    /**
     * Fetch existing medication requests for the current patient and encounter
     */
    async fetchExistingMedicationRequests(): Promise<void> {
        try {
            const patientId = await firstValueFrom(this.stateService.currentPatientId$);
            const currentEncounter = this.stateService.currentEncounter.value;
            const encounterId = currentEncounter?.id;

            if (!patientId) {
                this.errorService.openandCloseError('No patient selected');
                return;
            }

            if (!encounterId) {
                this.errorService.openandCloseError('No encounter selected');
                return;
            }

            const response = await firstValueFrom(
                this.http.get<any>(`${this.backendUrl}/MedicationRequest?patient=${patientId}&encounter=Encounter/${encounterId}&status=active`)
            );

            this.existingMedicationRequests = response.entry?.map((entry: any) => ({
                reference: `MedicationRequest/${entry.resource.id}`,
                display: entry.resource.medicationCodeableConcept?.text ||
                    entry.resource.medicationCodeableConcept?.coding?.[0]?.display ||
                    `Medication Request ${entry.resource.id}`,
                resource: entry.resource
            })) || [];
        } catch (error) {
            console.error('Error fetching medication requests:', error);
            this.errorService.openandCloseError('Failed to fetch medication requests');
        }
    }    /**
     * Handle task focus option selection
     */
    async onTaskFocusOptionSelected(option: string): Promise<void> {
        this.selectedTaskFocusOption = option;

        switch (option) {
            case 'select-existing-service-request':
                await this.fetchExistingServiceRequests();
                break;
            case 'select-existing-medication-request':
                await this.fetchExistingMedicationRequests();
                break;
            case 'create-new-service-request':
            case 'create-new-medication-request':
                // Will be handled when user creates the request
                break;
        }
    }

    /**
     * Handle care plan form submission
     * Extracts the care plan ID from the submitted data and enables task creation
     */
    onCarePlanSubmitted(formValues: any): void {
        console.log('Care plan submitted:', formValues);

        // Build FHIR CarePlan resource from form values
        const carePlan = this.buildCarePlanResource(formValues);

        // Submit to backend
        this.submitCarePlan(carePlan);
    }

    /**
     * Build FHIR CarePlan resource from form values
     */
    auth = inject(AuthService)
    private buildCarePlanResource(formValues: any): any {
        const patientId = this.stateService.currentPatientId$.getValue();
        const encounterId = this.stateService.currentEncounter.getValue()?.id;

        return {
            resourceType: 'CarePlan',
            status: formValues.status || 'active',
            intent: formValues.intent || 'plan',
            title: formValues.title || 'Patient Care Plan',
            description: formValues.description,
            subject: { reference: `Patient/${patientId}` },
            encounter: encounterId ? { reference: `Encounter/${encounterId}` } : undefined,

            created: new Date().toISOString(),
            author: this.auth.user?.getValue()?.['fhirResourceId'] ? { reference: `Practitioner/${this.auth.user?.getValue()?.['fhirResourceId']}` } : undefined,
            // category: formValues.category ? [this.utilityService.('CodeableConcept', formValues.category)] : undefined,
            // addresses: formValues.addresses || [],
            // goal: formValues.goals || [],
            // activity: formValues.activities || []
        };
    }

    /**
     * Submit CarePlan to backend and extract ID
     */
    notificationService = inject(NotificationService)
    private submitCarePlan(carePlan: any): void {
        //vaidate against status, intent,value and description
        if (!carePlan.status || !carePlan.intent || !carePlan.title) {
            this.errorService.openandCloseError('Please fill in all required fields: status, intent, title, and description.');
            return;
        }

        this.http.post(`${this.backendUrl}/CarePlan`, carePlan).subscribe({
            next: (result: any) => {
                console.log('✅ CarePlan created successfully:', result);

                // Extract the care plan ID from response
                this.carePlanId = result.id || this.extractIdFromLocation(result);

                if (this.carePlanId) {
                    console.log('📋 CarePlan ID extracted:', this.carePlanId);

                    // Update state service
                    this.stateService.persistPatientResource(result, 'saved');

                    // Show success message
                    this.notificationService.success(`Care Plan created successfully (ID: ${this.carePlanId})`);

                    // Auto-show task form since care plan is now available
                    this.displayTaskForm = true;
                } else {
                    this.errorService.openandCloseError('Care Plan created but ID could not be extracted');
                }
            },
            error: (err) => {
                console.error('❌ CarePlan creation failed:', err);
                this.errorService.openandCloseError(
                    `Failed to create care plan: ${err.error?.issue?.[0]?.diagnostics || err.message || 'Unknown error'}`
                );
            }
        });
    }

    /**
     * Extract resource ID from Location header (if backend returns 201 Created with Location)
     */
    private extractIdFromLocation(response: any): string | null {
        // Check if response has headers
// $#$        // /* alert(JSON.stringify(response) */)
        if (response?.headers) {
            const location = response.headers.get('Location');
            if (location) {
                // Extract ID from Location header like "https://server.com/CarePlan/12345"
                const match = location.match(/CarePlan\/([^\/\?]+)/);
                return match ? match[1] : null;
            }
        }

        // Fallback: check if response body has id
        return response?.id || null;
    }

    /**
     * Toggle task form visibility (renamed from showTaskForm for clarity)
     */
    toggleTaskForm(): void {
        if (!this.carePlanId) {
            this.errorService.openandCloseError('Please submit the care plan form first to enable task creation');
            return;
        }

        this.displayTaskForm = !this.displayTaskForm;
    }

    /**
     * Close the dialog if opened as a dialog
     */
    closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }
}
